import React from 'react';
import { graphql } from 'gatsby';

import Helmet from 'react-helmet';
import { FiUploadCloud, FiLayout, FiCode } from 'react-icons/fi';
import DefaultLayout from '../layouts';
import * as styles from '../variants/variant2.module.styl';
import TrustBar from '../components/TrustBar';
import Button from '../components/Button';
import Block from '../components/ContentBlock';
import { AfterSubmissionSection } from '../variants/product-old';
import DynamicLandingPage from './dynamic-landing-page';

function get(object, path, fallback = '') {
  let cursor = object;
  const parts = path.split('.');

  for (const part of parts) {
    const result = cursor[part];
    if (result === undefined) {
      return fallback;
    }
    cursor = result;
  }
  return cursor || fallback;
}

export default class Page extends React.Component {
  render() {
    const content = this.props.data.paperformHelpLandingPages;
    if (content.dynamic) {
      return <DynamicLandingPage {...this.props} {...content} />;
    }

    return (
      <DefaultLayout
        title={content.meta_title}
        location={this.props.location}
        noFade
        fixedNav
        disclaimer={content?.disclaimer}
      >
        <Helmet titleTemplate={false}>
          <title>{content.meta_title}</title>
          <meta name="description" content={content.meta_description} />
          <meta property="og:title" content={content.meta_title} />
          <meta property="og:description" content={content.meta_description} />
        </Helmet>

        <div className="bg-white">
          <div className="bg-yellow pb-12 lg:bg-cloud bg-left bg-none bg-fixed lg:bg-[url(/website/logo-v3/graphics/2-5.svg)] bg-cover bg-top xl:bg-right">
            <div className="lg:min-h-[calc(100vh-50px)] pt-24 3xl:h-[900px] lg:max-h-[1024px] max-width p-0 lg:grid lg:grid-cols-2 items-center">
              <div className="lg:p-[50px] pt-[140px] lg:pt-[70px] flex-1 text-black relative">
                <h1 className="text-4xl lg:text-5xl lg:leading-[1.15]">
                  {content.title}
                </h1>
                <p className="max-w-full lg:mt-8 lg:w-[400px]">
                  {content.description}
                </p>
                {content.cta_html ? (
                  <div dangerouslySetInnerHTML={{ __html: content.cta_html }} />
                ) : (
                  <span>
                    <div className="hidden lg:block">
                      <Button yellow solid href="/register">
                        Get Started
                      </Button>
                    </div>
                    <div className="lg:hidden">
                      <Button black solid href="/register">
                        Get Started
                      </Button>
                    </div>
                    <p className="text-xs opacity-75 my-3">
                      Free for 7 days. No credit card needed.
                    </p>
                  </span>
                )}
              </div>
              <div className="flex items-center justify-center py-6 lg:pt-24" />
            </div>
          </div>
          <TrustBar />
          {content.features.map((feature, index) => {
            const align = index % 2 ? 'left' : 'right';

            return (
              <Block
                key={index}
                align={align}
                title={feature.title}
                description={feature.description}
                linkURL={feature.link_url}
                linkLabel={feature.link_text}
                shadow={feature.custom_image}
              >
                <img
                  className="lozad"
                  data-src={`https://img.paperform.co/fetch/w_1000,f_auto/${
                    feature.custom_image || feature.image
                  }`}
                  alt={feature.title}
                />
              </Block>
            );
          })}
          {content.include_automation ? (
            <AfterSubmissionSection hideIntegrations />
          ) : null}
          <div className={`${styles.bottomCTA}`}>
            <div className={styles.bottomCTAInner}>
              <h3 className="H1">
                {get(
                  content,
                  'end.title',
                  "Our customers love us. Here's why.",
                )}
              </h3>
              <div className={styles.bottomCTAColumns}>
                <div>
                  <FiUploadCloud />
                  <h4>{get(content, 'end.panel_1_title', 'Integrations')}</h4>
                  <p>
                    {get(
                      content,
                      'end.panel_1_description',
                      '2000+ In-app and Zapier integrations that  help you automate your workflows.',
                    )}
                  </p>
                </div>
                <div>
                  <FiLayout />
                  <h4>
                    {get(content, 'end.panel_2_title', 'Template Library')}
                  </h4>
                  <p>
                    {get(
                      content,
                      'end.panel_2_description',
                      'Over 300 designer templates to choose from.',
                    )}
                  </p>
                </div>
                <div>
                  <FiCode />
                  <h4>
                    {get(content, 'end.panel_3_title', 'Custom Solutions')}
                  </h4>
                  <p>
                    {get(
                      content,
                      'end.panel_3_description',
                      'Use custom HTML & CSS for greater flexibility. Use logic and calcuations to take control.',
                    )}
                  </p>
                </div>
              </div>
              <form
                className={styles.getStartedForm}
                action="/register"
                method="GET"
              >
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email address"
                />
                <button type="button">Get Started →</button>
              </form>
              <small>
                Start your 7 day FREE Trial · No CC Required · Cancel anytime
              </small>
            </div>
          </div>

          <div className={styles.verifiedBlock}>
            <img
              alt=""
              style={{ height: 50 }}
              className="lozad"
              data-src="https://img.paperform.co/fetch/w_1/https://s3-ap-southeast-2.amazonaws.com/paperform/u-1/0/2019-05-01/jr136m5/L_Color_Solid.svg"
            />
            <img
              alt=""
              style={{ height: 50 }}
              className="lozad"
              data-src="https://s3.amazonaws.com/braintree-badges/braintree-badge-wide-light.png"
            />
            <img
              alt=""
              style={{ height: 50 }}
              className="lozad"
              data-src="https://img.paperform.co/fetch/w_600,f_auto/https://connect.squareup.com/connect-web/assets/built-with-square/brand_white-edcf1cc8a10da45f6d0e67215261bcd24f83a48336be84307d3ca62314963adf.png"
            />
            <img
              alt=""
              style={{ height: 50 }}
              className="lozad"
              data-src="https://img.paperform.co/fetch/w_600,f_auto/https://www.paypalobjects.com/webstatic/mktg/logo-center/PP_Acceptance_Marks_for_LogoCenter_266x142.png"
            />
          </div>
        </div>
      </DefaultLayout>
    );
  }
}

export const query = graphql`
  query HelpLandingPageQuery($slug: String) {
    paperformHelpLandingPages(slug: { eq: $slug }) {
      id
      h1
      dynamic
      content {
        ...ComponentsFragment
        ...RotatorFragment
      }
      disclaimer
      noindex
      description
      meta_title
      meta_description
      slug
      title
      include_automation
      cta_html
      features {
        custom_image
        description
        image
        link_url
        link_text
        title
      }
      end {
        panel_1_title
        panel_1_description
        panel_2_title
        panel_2_description
        panel_3_title
        panel_3_description
        title
      }
    }
  }
`;
